import { useState } from 'react';

import { PlusOutlined } from '@ant-design/icons';
import { Button, Form, Input, message, Modal, Row, Select } from 'antd';
import { useTranslation } from 'react-i18next';

import { useCreateBrandMutation, CreateBrandInput } from '@/graphql/main';

type Props = {
    successCallback?: () => void;
};

const CreateBrandModal = ({ successCallback }: Props) => {
    const [createBrandMutation] = useCreateBrandMutation();
    const [form] = Form.useForm<CreateBrandInput>();
    const [isModalOpen, setIsModalOpen] = useState(false);
    const { t } = useTranslation('translation');

    const showModal = () => {
        setIsModalOpen(true);
    };

    const handleCancel = () => {
        setIsModalOpen(false);
    };

    const handleCreateBrand = async () => {
        try {
            const values = await form.validateFields();
            await createBrandMutation({
                variables: {
                    input: { ...values }
                },
                onCompleted: async () => {
                    successCallback?.();
                    message.success(t('CREATE_BRAND_SUCCESS'));
                },
                onError: async () => await message.error(t('CREATE_BRAND_FAIL'))
            });
            form.resetFields();
            setIsModalOpen(false);
        } catch {
            await message.error(t('CREATE_BRAND_FAIL'));
        } finally {
            form.resetFields();
        }
    };

    return (
        <>
            <Row justify="end" style={{ marginTop: 18 }}>
                <Button type="primary" icon={<PlusOutlined />} onClick={showModal}>
                    {t('NEW_BRAND')}
                </Button>
            </Row>
            <Modal
                title={t('CREATE_NEW_BRAND')}
                open={isModalOpen}
                onCancel={handleCancel}
                footer={[
                    <Button key="back" onClick={handleCancel}>
                        {t('CANCEL')}
                    </Button>,
                    <Button key="submit" type="primary" onClick={handleCreateBrand}>
                        {t('CREATE_BRAND')}
                    </Button>
                ]}>
                <Form form={form}>
                    <Form.Item
                        name="name"
                        label={t('NAME')}
                        labelCol={{ span: 24 }}
                        rules={[{ required: true, message: t('NAME_REQUIRED') }]}>
                        <Input placeholder="Name" />
                    </Form.Item>

                    <Form.Item
                        label={t('ACTIVE')}
                        name="isActive"
                        labelCol={{ span: 24 }}
                        rules={[{ required: true, message: t('ACTIVE_STATUS_REQUIRED') }]}>
                        <Select placeholder={t('ACTIVE')}>
                            <Select.Option value={true}>{t('YES')}</Select.Option>
                            <Select.Option value={false}>{t('NO')}</Select.Option>
                        </Select>
                    </Form.Item>
                </Form>
            </Modal>
        </>
    );
};

export default CreateBrandModal;
